import { Component } from '@theme/component';

/**
 * Köttbutiken: produktflikar (sections/kb-produktflikar.liquid).
 * Dator (≥ 750 px): flikar på rad, en panel i taget (is-aktiv), piltangenter mellan flikarna.
 * Mobil: dragspel, varje rubrik fäller ut sin panel (is-oppen).
 * Horizons on:click/on:keydown sätter event.target till elementet med attributet.
 * En länk till #<fliknamn>, t.ex. #recept, öppnar den fliken.
 *
 * @typedef {{ flikar: HTMLButtonElement[], rubriker: HTMLButtonElement[], paneler: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class KbProduktflikarComponent extends Component {
  #dator = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    super.connectedCallback();
    this.#dator.addEventListener('change', this.#uppdateraRoller);
    window.addEventListener('hashchange', this.#oppnaFranAdress);
    this.#uppdateraRoller();
    this.#oppnaFranAdress();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#dator.removeEventListener('change', this.#uppdateraRoller);
    window.removeEventListener('hashchange', this.#oppnaFranAdress);
  }

  /** @param {MouseEvent} event */
  valj(event) {
    const index = this.refs.flikar.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    if (index >= 0) this.#visa(index);
  }

  /** @param {KeyboardEvent} event */
  tangent(event) {
    const { flikar } = this.refs;
    const nu = flikar.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    let ny = -1;
    if (event.key === 'ArrowRight') ny = (nu + 1) % flikar.length;
    else if (event.key === 'ArrowLeft') ny = (nu - 1 + flikar.length) % flikar.length;
    else if (event.key === 'Home') ny = 0;
    else if (event.key === 'End') ny = flikar.length - 1;
    if (ny < 0) return;
    event.preventDefault();
    this.#visa(ny);
    flikar[ny]?.focus();
  }

  /** @param {MouseEvent} event */
  vaxla(event) {
    const index = this.refs.rubriker.indexOf(/** @type {HTMLButtonElement} */ (event.target));
    const panel = this.refs.paneler[index];
    if (!panel) return;
    const oppna = !panel.classList.contains('is-oppen');
    this.#sattOppen(index, oppna);
    if (oppna) this.#sattAktiv(index);
  }

  /** Visar fliken på dator och fäller ut samma panel i mobil. @param {number} index */
  #visa(index) {
    this.#sattAktiv(index);
    this.refs.paneler.forEach((_, i) => this.#sattOppen(i, i === index));
  }

  /** @param {number} index */
  #sattAktiv(index) {
    this.refs.flikar.forEach((flik, i) => {
      const vald = i === index;
      flik.setAttribute('aria-selected', String(vald));
      flik.tabIndex = vald ? 0 : -1;
      this.refs.paneler[i]?.classList.toggle('is-aktiv', vald);
    });
  }

  /** @param {number} index @param {boolean} oppen */
  #sattOppen(index, oppen) {
    this.refs.paneler[index]?.classList.toggle('is-oppen', oppen);
    this.refs.rubriker[index]?.setAttribute('aria-expanded', String(oppen));
  }

  /** Flikpanel på dator, region under sin dragspelsrubrik i mobil. */
  #uppdateraRoller = () => {
    const dator = this.#dator.matches;
    this.refs.paneler.forEach((panel, i) => {
      const innehall = panel.querySelector('.kb-flikar__innehall');
      if (!innehall) return;
      innehall.setAttribute('role', dator ? 'tabpanel' : 'region');
      const etikett = dator ? this.refs.flikar[i] : this.refs.rubriker[i];
      if (etikett) innehall.setAttribute('aria-labelledby', etikett.id);
    });
  };

  #oppnaFranAdress = () => {
    const handle = decodeURIComponent(window.location.hash.slice(1));
    if (!handle) return;
    const index = this.refs.paneler.findIndex((panel) => panel.dataset.handle === handle);
    if (index < 0) return;
    this.#visa(index);
    // Vid sidladdning först när sidan laddat klart, annars hinner den inte bli scrollbar
    const rulla = () => requestAnimationFrame(() => this.scrollIntoView({ block: 'start' }));
    if (document.readyState === 'complete') rulla();
    else window.addEventListener('load', rulla, { once: true });
  };
}

if (!customElements.get('kb-produktflikar-component')) {
  customElements.define('kb-produktflikar-component', KbProduktflikarComponent);
}
